import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { themecolors } from "../../Layout/theme";

const SearchDropdown = ({
  apiUrl,
  queryKey = "q",
  value,
  onChange,
  label = "Search",
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiUrlRef = useRef(apiUrl);
  const queryKeyRef = useRef(queryKey);

  apiUrlRef.current = apiUrl;
  queryKeyRef.current = queryKey;

  const fetchData = useCallback(async (searchText = "") => {
    setLoading(true);
    setError(null);

    try {
      const currentApiUrl = apiUrlRef.current;
      const url =
        typeof currentApiUrl === "function"
          ? currentApiUrl(searchText)
          : currentApiUrl;
      const response = await axios.get(url, {
        params:
          typeof currentApiUrl === "function"
            ? {}
            : { [queryKeyRef.current]: searchText },
      });
      const payload = response.data?.data ?? response.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      setOptions(rows);
    } catch (err) {
      console.error("Search API error:", err);
      setOptions([]);
      setError("Failed to load results");
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce(fetchData, 400), [fetchData]);

  useEffect(() => {
    fetchData();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch, fetchData]);

  const filter = createFilterOptions({
    stringify: (option) =>
      `${option?.contractNumber || ""} ${option?.name || ""}`,
  });

  return (
    <Autocomplete
      size="small"
      options={
        value && !(options || []).some((opt) => opt?.id === value?.id)
          ? [value, ...(options || [])]
          : options || []
      }
      value={value || null}
      onChange={(e, newValue) => onChange(newValue || null)}
      getOptionLabel={(option) => {
        if (!option || typeof option !== "object") return "";
        const name = option.name || option.label || option.accountName || "";
        const contract = option.contractNumber || "";
        return contract ? `${name} - ${contract}` : name;
      }}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      filterOptions={filter}
      loading={loading}
      noOptionsText={error || "No options found"}
      onInputChange={(e, newValue, reason) => {
        if (reason === "input") {
          debouncedFetch(newValue);
        } else if (reason === "clear") {
          debouncedFetch.cancel();
          setOptions([]);
          fetchData();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error}
          size="small"
          sx={{
            "& .MuiInputLabel-root": {
              fontSize: 12,
                "&.Mui-focused": {
                  color: themecolors?.["500"],
                },
            },

            "& .MuiOutlinedInput-root": {
              minHeight: 30,
              borderRadius: 1,
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themecolors?.["500"],
                },
            },
            "& .MuiOutlinedInput-input": {
              padding: "8px 8px",
              fontSize: 12,
              lineHeight: 1.2,
              boxSizing: "border-box",
              borderColor: themecolors?.["500"],
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: 1,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && (
                  <CircularProgress
                    size={18}
                    style={{ color: themecolors?.["500"] }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchDropdown;
